import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'cpg_cookie_consent';

export function getCookieConsent() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
}

export function setCookieConsent(preferences) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...preferences,
      timestamp: new Date().toISOString(),
    }));
    applyGtagConsent(preferences);
  } catch {}
}

export function applyGtagConsent(preferences) {
  if (!window.gtag) return;
  window.gtag('consent', 'update', {
    analytics_storage:   preferences.analytics   ? 'granted' : 'denied',
    ad_storage:          preferences.advertising ? 'granted' : 'denied',
    ad_user_data:        preferences.advertising ? 'granted' : 'denied',
    ad_personalization:  preferences.advertising ? 'granted' : 'denied',
  });
}

export default function CookieBanner() {
  const [show, setShow]             = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [prefs, setPrefs]           = useState({ analytics: true, advertising: true });

  useEffect(() => {
    const consent = getCookieConsent();
    if (consent) {
      applyGtagConsent(consent);
    } else {
      setTimeout(() => setShow(true), 1200);
    }
  }, []);

  const acceptAll  = () => { setCookieConsent({ analytics: true, advertising: true });   setShow(false); };
  const rejectAll  = () => { setCookieConsent({ analytics: false, advertising: false }); setShow(false); };
  const saveCustom = () => { setCookieConsent(prefs); setShow(false); setShowCustom(false); };

  if (!show) return null;

  return (
    <>
      {/* Backdrop for customize modal */}
      {showCustom && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          zIndex: 9998, backdropFilter: 'blur(2px)',
        }} onClick={() => setShowCustom(false)} />
      )}

      {/* ── Main banner — full-width slim bar ── */}
      {!showCustom && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: 'var(--surface)',
          borderTop: '0.5px solid var(--border2)',
          zIndex: 9999,
          fontFamily: 'var(--font)',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
        }}>
          <div style={{
            maxWidth: 1100,
            margin: '0 auto',
            padding: '10px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
          }}>
            {/* Text */}
            <div style={{ flex: 1, minWidth: 220, fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
              <span style={{ fontSize: 14, marginRight: 6 }}>🍪</span>
              We use essential cookies to run the site, and optional cookies for analytics and ads.{' '}
              <Link to="/legal/cookies" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                Cookie Policy
              </Link>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
              <button onClick={() => setShowCustom(true)} style={{
                background: 'none', color: 'var(--dim)', border: 'none',
                fontFamily: 'var(--font)', fontSize: 12, cursor: 'pointer',
                padding: '6px 2px', textDecoration: 'underline', whiteSpace: 'nowrap',
              }}>
                Customize
              </button>
              <button onClick={rejectAll} style={{
                background: 'var(--surface2)', color: 'var(--muted)',
                border: '0.5px solid var(--border2)',
                borderRadius: 7, padding: '6px 14px',
                fontFamily: 'var(--font)', fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
                Reject non-essential
              </button>
              <button onClick={acceptAll} style={{
                background: '#7f77dd', color: '#fff', border: 'none',
                borderRadius: 7, padding: '6px 16px',
                fontFamily: 'var(--font)', fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Customize modal ── */}
      {showCustom && (
        <div style={{
          position: 'fixed', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'var(--surface)',
          border: '0.5px solid var(--border2)',
          borderRadius: 14, padding: '24px 22px',
          width: 'calc(100% - 32px)', maxWidth: 420,
          zIndex: 9999,
          fontFamily: 'var(--font)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text)', marginBottom: 3 }}>
            Cookie preferences
          </div>
          <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 18, lineHeight: 1.5 }}>
            Choose which cookies you allow. Essential cookies cannot be disabled.
          </div>

          {[
            { key: null,          label: 'Essential',    desc: 'Required for login, security, and core functionality. Always active.', locked: true },
            { key: 'analytics',   label: 'Analytics',    desc: 'Google Analytics — helps us understand how the app is used. No cross-site tracking.', locked: false },
            { key: 'advertising', label: 'Advertising',  desc: 'Google Ads — conversion tracking and relevant ads. May track across sites.', locked: false },
          ].map(item => (
            <div key={item.label} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              padding: '12px 0', borderBottom: '0.5px solid var(--border)',
            }}>
              <div style={{ flex: 1, paddingRight: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', marginBottom: 2 }}>
                  {item.label}
                  {item.locked && <span style={{ fontSize: 10, color: 'var(--dim)', marginLeft: 7, fontWeight: 400 }}>Always on</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--dim)', lineHeight: 1.5 }}>{item.desc}</div>
              </div>
              <div
                onClick={() => !item.locked && item.key && setPrefs(p => ({ ...p, [item.key]: !p[item.key] }))}
                style={{
                  width: 34, height: 19, borderRadius: 10, flexShrink: 0,
                  background: (item.locked || (item.key && prefs[item.key])) ? '#7f77dd' : 'var(--border2)',
                  position: 'relative', cursor: item.locked ? 'default' : 'pointer',
                  transition: 'background 0.2s', marginTop: 2,
                  opacity: item.locked ? 0.6 : 1,
                }}>
                <div style={{
                  width: 13, height: 13, borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: 3,
                  left: (item.locked || (item.key && prefs[item.key])) ? 18 : 3,
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
            </div>
          ))}

          <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
            <button onClick={saveCustom} style={{
              flex: 1, background: '#7f77dd', color: '#fff', border: 'none',
              borderRadius: 7, padding: '9px',
              fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
            }}>
              Save preferences
            </button>
            <button onClick={() => setShowCustom(false)} style={{
              background: 'none', color: 'var(--dim)', border: '0.5px solid var(--border2)',
              borderRadius: 7, padding: '9px 14px',
              fontFamily: 'var(--font)', fontSize: 13, cursor: 'pointer',
            }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}